import ReceiptItemsList from '@/components/receipt/details/ReceiptItemsList';
import ReceiptSummary from '@/components/receipt/details/ReceiptSummary';
import ReceiptFooter from '@/components/receipt/details/ReviewBlock';
import TransactionBlock from '@/components/receipt/details/TransactionBlock';
import { DividerWithPill } from '@/components/receipt/DividerWithPill';
import useOpenApiClient from '@/hooks/useOpenApiClient';
import { useQuery } from '@tanstack/react-query';
import Close from '@/assets/icons/X.svg?react';
import RejectedReceiptLarge from '@/assets/icons/rejected_receipt_large.svg?react';
import NotAcceptedReceipt from '@/assets/icons/not_acceipted_receipt.svg?react';
import { useLocationWithGoBack } from '@/hooks/useLocationWithGoBack';
import LineItemDetailsSheet from '../components/receipt/details/LineItemDetailsSheet';
import { useState } from 'react';
import { RequestReviewSheet } from '@/components/receipt/details/RequestReviewSheet';
import InReviewBanner from '@/components/receipt/details/InReviewBanner';
import { Link, useParams } from 'wouter';
import { components } from '@/types/api';
import ProcessingBanner from '@/components/receipt/details/ProcessingBanner';
import { format } from 'date-fns';

type LineItem = components['schemas']['LineItem'];

export default function Receipt() {
  const { apiFetch, userRole } = useOpenApiClient();
  const { receiptId } = useParams<'/receipts/:receiptId'>();
  const [, setLocation] = useLocationWithGoBack();

  const [lineItemDetailsToShow, setLineItemDetailsToShow] = useState<LineItem | null>(null);
  const [isRequestReviewSheetOpen, setIsRequestReviewSheetOpen] = useState(false);

  const receiptDetailsQuery = useQuery({
    queryKey: ['v1', 'receipts', receiptId],
    queryFn: () =>
      apiFetch.GET('/receipts/{receipt_id}', {
        params: {
          path: {
            receipt_id: receiptId,
          },
        },
      }),
    refetchInterval: 5 * 1000,
    select: (data) => {
      return data.data?.data;
    },
    enabled: !!receiptId,
  });

  const onLineItemDetailsSheetClose = () => {
    setLineItemDetailsToShow(null);
  };
  const toggleRequestReviewSheet = () => {
    setIsRequestReviewSheetOpen((prev) => {
      if (receiptDetailsQuery.data?.status === 'review_requested') {
        return false;
      }
      return !prev;
    });
  };

  if (receiptDetailsQuery.data?.status === 'rejected') {
    return <RejectedReceipt receipt={receiptDetailsQuery.data} />;
  }

  return (
    <>
      <div className="min-h-screen overflow-hidden px-2.5 py-5">
        {!receiptDetailsQuery.isLoading && receiptDetailsQuery.data && (
          <>
            <div className="mx-auto flex max-h-[calc(100vh-50px)] max-w-md flex-col overflow-hidden rounded-t-xl">
              <div className="shrink-0 bg-white">
                <div className="flex flex-row items-center px-5 pt-3 pb-5">
                  <h1 className="grow text-center text-xl font-semibold">Receipt</h1>
                  <button className="flex-none" onClick={() => setLocation(`/receipts`)}>
                    <Close />
                  </button>
                </div>
                <TransactionBlock receipt={receiptDetailsQuery.data} />

                {(receiptDetailsQuery.data.status === 'review_requested' ||
                  receiptDetailsQuery.data.status === 'in_review') && <InReviewBanner />}
                {receiptDetailsQuery.data.status === 'processing' && <ProcessingBanner />}

                <div className="pt-4 pb-3">
                  <DividerWithPill
                    className="border-background"
                    pillClassName="bg-background text-primary"
                  >
                    {userRole === 'live' ? 'TOTALS' : 'ENRICHED'}
                  </DividerWithPill>
                </div>
                <ReceiptSummary receipt={receiptDetailsQuery.data} />

                {receiptDetailsQuery.data.line_items?.length ? (
                  <div className="pt-4 pb-3">
                    <DividerWithPill
                      className="border-primary-platinum"
                      pillClassName="bg-primary-platinum text-gray-500"
                    >
                      ITEMS BOUGHT
                    </DividerWithPill>
                  </div>
                ) : null}
              </div>
              <div className="scrollbar-hide no-scrollbar grow overflow-y-auto bg-white">
                {receiptDetailsQuery.data.status != 'processing' && (
                  <ReceiptItemsList
                    setLineItemDetails={setLineItemDetailsToShow}
                    receipt={receiptDetailsQuery.data}
                  />
                )}
                {receiptDetailsQuery.data.status != 'review_requested' &&
                  receiptDetailsQuery.data.status != 'processing' &&
                  receiptDetailsQuery.data.status != 'in_review' && (
                    <ReceiptFooter
                      receipt={receiptDetailsQuery.data}
                      onRequestReview={toggleRequestReviewSheet}
                    />
                  )}

                <div className="bg-background mt-2 h-4 w-full bg-[url('@/assets/icons/zigzag.png')] bg-repeat-x" />
              </div>
            </div>
          </>
        )}
      </div>
      <LineItemDetailsSheet
        lineItem={lineItemDetailsToShow}
        onClose={onLineItemDetailsSheetClose}
      />
      <RequestReviewSheet open={isRequestReviewSheetOpen} onClose={toggleRequestReviewSheet} />
    </>
  );
}

function RejectedReceipt({ receipt }: { receipt: components['schemas']['ReceiptOutput'] }) {
  const [, setLocation] = useLocationWithGoBack();
  return (
    <div className="min-h-screen overflow-hidden px-2.5 py-5">
      <div className="mx-auto flex max-h-[calc(100vh-50px)] max-w-md flex-col overflow-hidden rounded-t-xl">
        <div className="shrink-0 bg-white pb-18">
          <div className="flex flex-row items-center px-5 pt-3 pb-5">
            <h1 className="grow text-center text-xl font-semibold">Receipt</h1>
            <button className="flex-none" onClick={() => setLocation(`/receipts`)}>
              <Close />
            </button>
          </div>
          <div className="flex items-center gap-3 p-3">
            <NotAcceptedReceipt />
            <div>
              <strong>Receipt not accepted</strong>
              <div className="flex items-center gap-2">
                <p className="text-primary-dark/50 text-sm">
                  Processed {format(new Date(receipt.created_at), 'MM/dd/yyyy')}
                </p>
                <Link href={`/receipts/${receipt.id}/view-image`} className="text-primary text-sm">
                  View image
                </Link>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center justify-center gap-2 p-3 text-center">
            <RejectedReceiptLarge />
            <strong>We weren’t able to accept this one.</strong>
            <p>This usually happens when the receipt has already been submitted.</p>
          </div>
        </div>
        <div className="bg-background h-4 w-full bg-[url('@/assets/icons/zigzag.png')] bg-repeat-x" />
      </div>
    </div>
  );
}
